import { composeTheme } from '@css-modules-theme/core'
import { announce } from '@react-aria/live-announcer'
import classNames from 'classnames'
import moment from 'moment-timezone'
import { FunctionComponent, useContext, useEffect } from 'react'
import StringToReactNode from '../StringToReactNode/StringToReactNode'
import moduleStyles from './OrderItem.module.css'
import NoteTooltip from './NoteTooltip'
import { toCurrency } from '@/utils/currency.utils'
import useTicketStyles from '@/hooks/useTicketStyles'
import { TICKETS_REMAINING_THRESHOLD } from '@/config/flags.config'
import { formatOnSaleDate, getUsersTimezone } from '@/utils/dates.utils'
import TheGreatContext from '@/app/_providers/TheGreatProvider/TheGreatContext'
import TXT from '@/messages'

const DEFAULT_ADDON_BG_COLOR = 'purple'

export type OrderItemProps = {
  /** accessibility: id of event details that describe the inc/dec action */
  describedBy: string
  /** quantity for Tier */
  quantity: number
  /** number of tickets remaining */
  remaining: number
  /** Disable the decrement button */
  decrementDisabled?: boolean
  /** is the ticket category within the sales window */
  onSaleOrWithinSalesWindow: boolean
  /** Item category */
  category: Transformed.TicketCategory | Transformed.TicketCategoryWithTiers
  /** Start time from the timeslot */
  startTime?: string
  /** special details about ticket type */
  note?: string
  /** ticketure summary (a short version of description) */
  summary?: Nullable<string>
  /**
   * When true, `note` is hidden from the item body and shown instead in a tooltip
   * that opens when the (i) icon next to the title is clicked.
   * @default false
   */
  noteAsTooltip?: boolean
  /** price of ticket */
  price: number
  /** type of ticket */
  title?: string
  /** optional classname */
  className?: string
  /** test id */
  dataTestId?: string
  /** click handler */
  handleClick: (action: QtyClickAction) => void

  hasReachedLimit: boolean

  cartUpdating: boolean

  startOffset: moment.Moment | undefined

  endOffset: moment.Moment | undefined

  /** Addon product image URL */
  image?: string
  /** Background color for the addon image container */
  imageBgColor?: string

  /**
 * Controls the number of decimal places that appear in the display price
 * @default 2
 */
  minimumFractionDigits?: number
}

const OrderTicket: FunctionComponent<OrderItemProps> = ({
  describedBy,
  quantity,
  remaining,
  handleClick,
  summary,
  note = '',
  noteAsTooltip = false,
  price,
  title = '',
  className,
  dataTestId = 'order-item',
  decrementDisabled = false,
  onSaleOrWithinSalesWindow,
  category,
  startTime = undefined,
  cartUpdating,
  startOffset,
  endOffset,
  image = undefined,
  imageBgColor = DEFAULT_ADDON_BG_COLOR,
  minimumFractionDigits = 2,
  hasReachedLimit,
}: OrderItemProps) => {
  const { isEvents } = useContext(TheGreatContext)

  const categorySoldOut = category.soldOut || (category.capacity.total > -1 && category.capacity.remaining <= 0)
  const hasNote = note.length > 0
  const showNoteInTooltip = noteAsTooltip && hasNote
  const descriptionInBody = noteAsTooltip ? summary : note

  const themeStyles = useTicketStyles(isEvents ? 'events' : 'exhibition')

  const styles = composeTheme([{ theme: moduleStyles }, { theme: themeStyles }])

  useEffect(() => {
    if (cartUpdating) return
    if (quantity > 0) {
      announce(TXT.a11y.ticketsAdded(quantity, title), 'polite')
    }
  }, [quantity, cartUpdating, title])

  // If no title don't show anything
  if (!title) return null

  const displayPrice = price === 0
    ? 'Free'
    : toCurrency(price, minimumFractionDigits)

  const isDiscounted = displayPrice === 'Free'

  const now = moment()
  // Sales window dates are shown in the user's timezone, so they don't have to convert them
  const usersTimezone = getUsersTimezone()
  const startOffsetElapsed = !!startOffset && startOffset.isBefore(now)
  const endOffsetElapsed = !!endOffset && endOffset.isBefore(now)

  return (
    <div
      className={
        classNames(
          { 'on-sale': onSaleOrWithinSalesWindow },
          'meow--ticket-wrapper',
          className,
          styles.TierWrapper,
        )
      }
      data-testid={dataTestId}
      role="region"
      tabIndex={0}
    >
      {image
        && (
          <div className={styles.ImageContainer} style={{ '--bg-color': imageBgColor } as React.CSSProperties}>
            <img
              src={image}
              className={styles.Image}
              alt="product"
              data-testid="order-items-image"
            />
          </div>
        )}
      <div
        className={styles.Tier}
        data-testid={`${dataTestId}-title`}
      >
        {title}
        {showNoteInTooltip && (
          <NoteTooltip
            category={category}
            dataTestId={dataTestId}
            note={note}
            styles={styles}
            title={title}
          />
        )}
      </div>
      <div
        className={styles.PriceWrap}
        data-testid={`${dataTestId}-price`}
        title={TXT.a11y.ticketPrice(title)}
      >
        <span
          className={
            classNames({ [styles.DiscountedPrice]: isDiscounted })
          }
          data-testid={isDiscounted ? `${dataTestId}-discount-price` : ''}
        >
          {displayPrice}
        </span>
      </div>
      <div className={styles.TierDescription}>
        {/* In the tooltip arm the description moves into the tooltip, so the body shows the summary instead. */}
        {descriptionInBody && (
          <StringToReactNode
            className={styles.NoteContainer}
            data-testid={`${dataTestId}-${noteAsTooltip ? 'summary' : 'note'}`}
            incomingString={descriptionInBody}
          />
        )}
        {/**
         * If not sold out and there are less than the TICKETS_REMAINING_THREHOLD (but not 0), display a message
         * I've seen a few times that we can have 0 remaining, but not yet marked as sold out
         */}
        {onSaleOrWithinSalesWindow && !categorySoldOut && remaining <= TICKETS_REMAINING_THRESHOLD && remaining > 0 && (
          <div className={styles.TicketNotice} data-testid="order-item-remaining-tickets">
            {TXT.ticketSelection.ticketsLeft(remaining)}
          </div>
        )}
        {/** Sold out */}
        {categorySoldOut && (
          <div className={classNames(styles.TicketNotice, styles.SoldOut)}>
            <span className={styles.Uppercase}>{TXT.buttons.soldOut}</span>
          </div>
        )}
        {/** on sale with endOffset */}
        {onSaleOrWithinSalesWindow && endOffset && !endOffsetElapsed && startTime && (
          <div className={styles.TicketNotice} data-testid="ticket-sale-ends-date">
            <span className={styles.Uppercase}>{TXT.ticketSelection.saleEnds}</span>
            {': '}
            {formatOnSaleDate(endOffset, usersTimezone)}
          </div>
        )}
        {/** sales window is coming up */}
        {!onSaleOrWithinSalesWindow && startOffset && !startOffsetElapsed && !endOffsetElapsed && startTime && (
          <div className={styles.TicketNotice} data-testid="ticket-on-sale-date">
            <span className={styles.Uppercase}>{TXT.ticketSelection.onSale}</span>
            {': '}
            {formatOnSaleDate(startOffset, usersTimezone)}
          </div>
        )}
        {/** sales window is elapsed */}
        {!onSaleOrWithinSalesWindow
          && endOffset
          && endOffsetElapsed && (
          <div className={styles.TicketNotice}>
            <span className={styles.Uppercase}>{TXT.ticketSelection.noLongerAvailable}</span>
          </div>
        )}
      </div>

      <div
        data-id="price-qty-wrapper"
        className={styles.PriceQtyWrapper}
      >
        {!onSaleOrWithinSalesWindow && (
          <div className={classNames('no-sale-spacer', styles.Spacer)} />
        )}
        {onSaleOrWithinSalesWindow && (
          <div className={styles.QtyControl}>
            <button
              data-testid={`${dataTestId}-increase`}
              type="button"
              className={classNames(
                { [styles.Disabled]: categorySoldOut || hasReachedLimit },
                styles.IncrementButton,
              )}
              disabled={categorySoldOut || hasReachedLimit}
              onClick={() => handleClick && handleClick('increment')}
              title={TXT.a11y.addTicket(title)}
              aria-labelledby={`${describedBy}`}
            >
              <span>+</span>
            </button>

            <div
              data-testid={`${dataTestId}-quantity`}
              className={styles.QtyValue}
              title={TXT.a11y.ticketQuantity(title)}
            >
              {quantity}
            </div>

            <button
              data-testid={`${dataTestId}-decrease`}
              type="button"
              className={classNames({ [styles.Disabled]: quantity <= 0 || decrementDisabled }, styles.DecrementButton)}
              disabled={quantity <= 0 || decrementDisabled}
              onClick={() => handleClick && handleClick('decrement')}
              title={TXT.a11y.removeTicket(title)}
              aria-labelledby={describedBy}
            >
              <span>-</span>
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default OrderTicket
